import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";

// Free and ungated, deliberately. Nothing on a learner's own machine can be
// verified from a remote lab, so installation was never going to be a lab —
// and keeping it open costs nothing while helping people find the course.

const Code = ({ children }) => (
  <pre className="mt-3 p-4 bg-slate-900 text-slate-100 rounded-lg text-xs font-mono overflow-x-auto">
    <code>{children}</code>
  </pre>
);

const Section = ({ id, n, title, children }) => (
  <section id={id} className="pt-12 border-t border-slate-200 dark:border-white/10" data-testid={`setup-${id}`}>
    <div className="flex items-baseline gap-3">
      <span className="font-mono text-xs text-slate-400 dark:text-zinc-600">{n}</span>
      <h2 className="text-lg font-medium text-slate-900 dark:text-zinc-100">{title}</h2>
    </div>
    <div className="mt-4 space-y-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
      {children}
    </div>
  </section>
);

export default function ClaudeCodeSetup() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="claude-setup-page">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <p className="font-mono text-xs text-sky-700 dark:text-sky-400 tracking-wide">FREE GUIDE</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-semibold text-slate-900 dark:text-zinc-100 tracking-tight">
          Installing Claude Code
        </h1>
        <p className="mt-5 text-base md:text-lg text-slate-600 dark:text-zinc-400 leading-relaxed">
          Five places you can run it, and what each is actually good for. No account with
          us needed to read this — the ILFAL labs give you a pre-configured workstation, so
          this is for your own machine.
        </p>

        <div className="mt-10 p-5 border border-slate-200 dark:border-white/10 rounded-lg bg-slate-50/60 dark:bg-white/[0.03] text-sm text-slate-600 dark:text-zinc-400 leading-relaxed">
          <span className="font-medium text-slate-900 dark:text-zinc-100">Before any of them:</span> you need an
          Anthropic API key or a Claude subscription that includes Claude Code. Create a key
          at{" "}
          <a
            href="https://console.anthropic.com/settings/keys"
            target="_blank"
            rel="noreferrer"
            className="text-slate-900 dark:text-zinc-100 font-medium underline decoration-slate-300 hover:decoration-slate-900"
          >
            console.anthropic.com/settings/keys
          </a>
          . Usage is billed to you per token — a beginner session is usually cents.
        </div>

        <div className="mt-6 space-y-0">
          <Section id="terminal" n="01" title="Terminal — the primary way">
            <p>
              Claude Code is a terminal program first; every other option below wraps this.
              Needs Node.js 18 or newer. macOS, Linux, or Windows via WSL.
            </p>
            <Code>{`npm install -g @anthropic-ai/claude-code

# authenticate, once
claude
# then type: /login
# ...or use an API key directly:
export ANTHROPIC_API_KEY=sk-ant-...

# start working, from inside your project
cd ~/my-project
claude`}</Code>
            <p>
              <span className="font-medium text-slate-900 dark:text-zinc-100">If `claude` is not found</span>{" "}
              after install, your npm global bin is not on your PATH. Run{" "}
              <code className="font-mono text-xs bg-slate-100 dark:bg-white/10 px-1.5 py-0.5 rounded">
                npm bin -g
              </code>{" "}
              and add that directory to your shell profile.
            </p>
            <p>
              <span className="font-medium text-slate-900 dark:text-zinc-100">On Windows</span>, install inside
              WSL rather than PowerShell. Claude Code expects a POSIX shell, and the
              difference shows up as confusing tool failures rather than a clear error.
            </p>
          </Section>

          <Section id="vscode" n="02" title="VS Code">
            <p>
              Install the Claude Code extension from the marketplace, then open the panel
              with <kbd className="font-mono text-xs">Cmd/Ctrl+Esc</kbd>. It runs the same
              CLI underneath but passes your current selection and open files as context,
              and shows diffs in the editor's own review UI.
            </p>
            <p>
              Worth it if you review diffs visually. If you live in a terminal already, the
              extension adds little.
            </p>
          </Section>

          <Section id="jetbrains" n="03" title="JetBrains IDEs">
            <p>
              IntelliJ, PyCharm, WebStorm, GoLand and the rest: install the Claude Code
              plugin from Settings → Plugins, then restart the IDE. Same CLI, same context
              passing, diffs shown in the JetBrains diff viewer.
            </p>
            <p>
              The plugin needs the CLI installed first — step 01 is not optional here.
            </p>
          </Section>

          <Section id="desktop" n="04" title="Claude Desktop">
            <p>
              The desktop app is a chat client, not Claude Code, and the distinction is the
              whole point of lab 1: an agent reads files, runs commands and edits code in a
              loop. A chat window cannot touch your machine.
            </p>
            <p>
              Where Desktop does matter is MCP: it is the easiest place to add an MCP server
              and see what it exposes before you wire the same server into Claude Code.
              Lab 9 covers that.
            </p>
          </Section>

          <Section id="web" n="05" title="Claude on the web">
            <p>
              claude.ai runs no agent on your machine at all. Useful for thinking out loud,
              planning a refactor, or pasting an error you do not understand — none of which
              requires file access.
            </p>
            <p>
              If you are following this course, do the labs with the CLI. The habits that
              matter — permission modes, context budget, hooks — only exist where the agent
              has tools.
            </p>
          </Section>

          <Section id="verify" n="06" title="Confirming it works">
            <p>Three checks, in order. If one fails, fix it before the next.</p>
            <Code>{`claude --version          # the CLI is installed and on PATH
claude -p "say hello"     # authentication and billing are working
cd ~/my-project && claude # trust prompt appears once per new folder`}</Code>
            <p>
              <span className="font-medium text-slate-900 dark:text-zinc-100">
                "Credit balance is too low"
              </span>{" "}
              means the key is valid but the account has no funds — add billing in the
              console. That is a different failure from an invalid key, and the message is
              easy to misread.
            </p>
          </Section>
        </div>

        <div className="mt-14 pt-10 border-t border-slate-200 dark:border-white/10">
          <h2 className="text-lg font-medium text-slate-900 dark:text-zinc-100">Then learn to drive it</h2>
          <p className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed max-w-2xl">
            Installing it is the easy part. Claude Code 101 is ten labs on the rest:
            reading your own session, choosing a permission mode on purpose, managing a
            context window, giving a project a memory, and enforcing rules with hooks
            rather than asking politely.
          </p>
          <Link
            to="/courses/claude-code-101"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-md hover:bg-slate-800 hover:gap-3 transition-all"
            data-testid="setup-course-cta"
          >
            See the course <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-6 text-xs text-slate-500 dark:text-zinc-500">
            Official docs:{" "}
            <a
              href="https://docs.anthropic.com/en/docs/claude-code"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-slate-700 hover:text-slate-900 dark:text-zinc-100"
            >
              docs.anthropic.com <ExternalLink className="w-3 h-3" />
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
